import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Timezone identifiers for UK and Canadian regions
 */
export const TIMEZONES = {
  UK: 'Europe/London',
  CANADA_PACIFIC: 'America/Vancouver',
  CANADA_MOUNTAIN: 'America/Edmonton',
  CANADA_CENTRAL: 'America/Winnipeg',
  CANADA_EASTERN: 'America/Toronto',
  CANADA_ATLANTIC: 'America/Halifax',
  CANADA_NEWFOUNDLAND: 'America/St_Johns',
} as const;

/**
 * Human-readable timezone labels
 */
export const TIMEZONE_LABELS: Record<keyof typeof TIMEZONES, string> = {
  UK: 'UK/London',
  CANADA_PACIFIC: 'Pacific (Vancouver/Victoria)',
  CANADA_MOUNTAIN: 'Mountain (Calgary/Edmonton)',
  CANADA_CENTRAL: 'Central (Winnipeg)',
  CANADA_EASTERN: 'Eastern (Toronto/Ottawa)',
  CANADA_ATLANTIC: 'Atlantic (Halifax)',
  CANADA_NEWFOUNDLAND: "Newfoundland (St. John's)",
};

/**
 * Format options for different date/time displays
 */
export const DATE_FORMAT = 'MMM d, yyyy';
export const TIME_FORMAT = 'h:mm a';
export const DATETIME_FORMAT = 'MMM d, yyyy h:mm a';
export const DATETIME_WITH_TIMEZONE_FORMAT = 'MMM d, yyyy h:mm a zzz';

/**
 * Convert a UTC date to various timezones
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param formatString - Optional format string (defaults to DATETIME_WITH_TIMEZONE_FORMAT)
 * @returns Array of timezone conversions
 */
export function convertToTimezones(
  utcDate: Date | string | number,
  formatString: string = DATETIME_WITH_TIMEZONE_FORMAT
): Array<{ timezone: string; label: string; formatted: string }> {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return Object.entries(TIMEZONES).map(([key, tz]) => ({
    timezone: tz,
    label: TIMEZONE_LABELS[key as keyof typeof TIMEZONES],
    formatted: formatInTimeZone(date, tz, formatString),
  }));
}

/**
 * Format a UTC date in a specific timezone
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param timezone - The IANA timezone identifier
 * @param formatString - The format string (defaults to DATETIME_WITH_TIMEZONE_FORMAT)
 * @returns Formatted date string
 */
export function formatInTimezone(
  utcDate: Date | string | number,
  timezone: string,
  formatString: string = DATETIME_WITH_TIMEZONE_FORMAT
): string {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return formatInTimeZone(date, timezone, formatString);
}

/**
 * Format a date as UTC with explicit UTC label
 * @param utcDate - The UTC date (Date object, ISO string, or timestamp)
 * @param formatString - The format string (defaults to DATETIME_FORMAT)
 * @returns Formatted UTC date string
 */
export function formatAsUTC(
  utcDate: Date | string | number,
  formatString: string = DATETIME_FORMAT
): string {
  const date =
    typeof utcDate === 'string' || typeof utcDate === 'number' ? new Date(utcDate) : utcDate;

  return format(date, formatString);
}
